/**
 * Autofix for a Baseline Accounting project: the same review IAS accounting runs, on the
 * reading the project was imported from, placed in the cells again.
 *
 * A run is kept only if it scores better than the project already does - closer to
 * balancing first, then to what the filing prints - and every run is listed, kept or not.
 * Every cell the user changed from what was placed is kept as the user's. A kept run is
 * saved as a version, so it can be restored from Versions.
 */

import { useCallback, useEffect, useState } from 'react';
import { BASE, headers, useMAccountingStore } from '../../../store/m-accounting';
import type { AutofixRun } from '../../../types/financial-model-v2';
import { formatMoney } from './MGrid';

interface MAutofixStatus {
  status: string;
  progress: string;
  error: string | null;
  runs: AutofixRun[];
  available: boolean;
  userCells: number;
}

export function MAutofix() {
  const project = useMAccountingStore((s) => s.project);
  const open = useMAccountingStore((s) => s.open);
  const [status, setStatus] = useState<MAutofixStatus | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!project) return null;
    const response = await fetch(`${BASE}/${project.id}/autofix-status`, { headers: headers() });
    if (!response.ok) return null;
    const body = (await response.json()) as MAutofixStatus;
    setStatus(body);
    return body;
  }, [project]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async () => {
    if (!project || running) return;
    setRunning(true);
    setError('');
    try {
      const started = await fetch(`${BASE}/${project.id}/autofix/start`, {
        method: 'POST',
        headers: headers(),
      });
      if (!started.ok) {
        setError(`Autofix could not start (${started.status}).`);
        return;
      }
      const deadline = Date.now() + 30 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const task = await load();
        if (!task) {
          setError('Lost track of Autofix - please retry.');
          return;
        }
        if (task.status === 'error') {
          setError(task.error ?? 'Autofix failed.');
          return;
        }
        if (task.status === 'done') {
          await open(project.id); // a kept run changed the cells
          return;
        }
      }
      setError('Autofix is taking longer than expected - check back shortly.');
    } finally {
      setRunning(false);
    }
  };

  if (!project || !status) return null;
  const runs = status.runs ?? [];

  return (
    <section className="rounded-xl border border-gray-200 bg-white px-4 py-3" aria-label="Autofix">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Autofix</h3>
          <p className="mt-0.5 text-xs text-gray-600">
            {status.available
              ? `Reviews the reading this project came from against the filing, as an accountant would, and places it again. A run is kept only if it is better; the cells you changed are never overwritten${status.userCells ? ` (${status.userCells} kept)` : ''}.`
              : 'Autofix reviews the filing a project was imported from; import the PDF again to use it.'}
          </p>
        </div>
        <button
          type="button"
          disabled={running || !status.available}
          onClick={() => void run()}
          className="shrink-0 rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
        >
          {running ? 'Working…' : runs.length ? 'Run Autofix again' : 'Autofix'}
        </button>
      </div>
      {running && status.progress && <p className="mt-2 text-xs text-gray-600">{status.progress}</p>}
      {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
      {runs.length > 0 && (
        <ul className="mt-2 divide-y divide-black/5">
          {[...runs].reverse().map((r) => (
            <li key={r.run} className="py-2 text-xs text-gray-700">
              <span className="text-sm text-gray-900">Run {r.run} </span>
              <span className={r.kept ? 'text-green-700' : 'text-gray-500'}>
                {r.kept ? '- kept' : '- not kept'}
              </span>
              <div className="mt-0.5">
                Out of balance {formatMoney(r.before?.balance ?? 0)} to{' '}
                {formatMoney(r.after?.balance ?? r.before?.balance ?? 0)}; away from the filing{' '}
                {formatMoney(r.before?.distance ?? 0)} to{' '}
                {formatMoney(r.after?.distance ?? r.before?.distance ?? 0)}
              </div>
              {r.note && <div className="mt-0.5 text-gray-600">{r.note}</div>}
              {r.bookings.filter((b) => b.kept).length > 0 && (
                <ul className="mt-1 list-disc pl-5">
                  {r.bookings.filter((b) => b.kept).map((b, i) => (
                    <li key={`${b.kind}-${i}`}>{b.reason || b.kind}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
